import React, { useEffect, useMemo, useRef, useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import applicationName from "./applicationName";
import t from "./translate";

function displayNameForUser(currentUser) {
  return [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ").trim() || currentUser?.email || applicationName();
}

function initialsForUser(currentUser) {
  const initials = `${currentUser?.firstName?.[0] || ""}${currentUser?.lastName?.[0] || ""}`.trim();
  return (initials || currentUser?.email?.[0] || "S").toUpperCase();
}

function csrfToken() {
  if (typeof document === "undefined") {
    return "";
  }

  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || "";
}

function oneSignalAppId() {
  if (typeof document === "undefined") {
    return "";
  }

  return document.querySelector("meta[name='school-bridge-onesignal-app-id']")?.getAttribute("content") || "";
}

function adminPushDeviceIdentifier() {
  if (typeof window === "undefined") {
    return "";
  }

  const storageKey = "schoolBridge.adminPushDeviceIdentifier";
  const existing = window.localStorage.getItem(storageKey);
  if (existing) {
    return existing;
  }

  const identifier = `admin-web-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
  window.localStorage.setItem(storageKey, identifier);
  return identifier;
}

function notificationIconClass(categoryKey) {
  switch (categoryKey) {
    case "messages":
      return "fa-comments";
    case "announcements":
      return "fa-bullhorn";
    case "homework":
      return "fa-book-open";
    case "class_updates":
      return "fa-newspaper";
    case "appointments":
      return "fa-calendar-check";
    default:
      return "fa-bell";
  }
}

async function readJson(response) {
  const payload = await response.json().catch(() => ({}));

  if (response.ok) {
    return payload;
  }

  const message = Array.isArray(payload.errors) && payload.errors.length > 0 ? payload.errors[0] : "Request failed";
  throw new Error(message);
}

function submitSignOut() {
  if (typeof document === "undefined") {
    return;
  }

  const form = document.createElement("form");
  form.method = "post";
  form.action = "/admin/sign_out";
  form.style.display = "none";

  const methodInput = document.createElement("input");
  methodInput.type = "hidden";
  methodInput.name = "_method";
  methodInput.value = "delete";

  const csrfInput = document.createElement("input");
  csrfInput.type = "hidden";
  csrfInput.name = "authenticity_token";
  csrfInput.value = csrfToken();

  form.appendChild(methodInput);
  form.appendChild(csrfInput);
  document.body.appendChild(form);
  form.submit();
}

const LOCALE_OPTIONS = {
  en: { flag: "🇬🇧", label: t("shared.admin_topbar.locale.english") },
  el: { flag: "🇬🇷", label: t("shared.admin_topbar.locale.greek") }
};

function switchLocale(nextLocale) {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("locale", nextLocale);
  window.location.assign(url.toString());
}

export default function AdminTopbar({ currentUser, workspaceSchool, currentLocale = "en", availableLocales = ["en", "el"] }) {
  const resolvedApplicationName = applicationName();
  const platformUser = currentUser?.platformUser;
  const pushEnabled = Boolean(currentUser?.id);
  const searchEnabled = platformUser || Boolean(workspaceSchool);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [localeOpen, setLocaleOpen] = useState(false);
  const [confirmSignOutOpen, setConfirmSignOutOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchError, setSearchError] = useState("");
  const searchContainerRef = useRef(null);
  const localeContainerRef = useRef(null);
  const notificationsContainerRef = useRef(null);
  const localeOptions = useMemo(
    () => availableLocales.map((locale) => ({ key: locale, ...(LOCALE_OPTIONS[locale] || { flag: "🏳️", label: locale.toUpperCase() }) })),
    [availableLocales]
  );
  const selectedLocale = localeOptions.find((option) => option.key === currentLocale) || localeOptions[0] || LOCALE_OPTIONS.en;

  useEffect(() => {
    if (!searchEnabled || searchTerm.trim().length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError("");
      return undefined;
    }

    let cancelled = false;
    setSearchOpen(true);
    setSearchLoading(true);
    setSearchError("");

    const timer = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: searchTerm.trim() });
        if (workspaceSchool?.id) {
          params.set("workspace_school_id", String(workspaceSchool.id));
        }

        const response = await fetch(`/api/v1/admin/search?${params.toString()}`, {
          headers: {
            Accept: "application/json"
          },
          credentials: "same-origin"
        });

        if (!response.ok) {
          throw new Error("Request failed");
        }

        const payload = await response.json();
        if (!cancelled) {
          setSearchResults(payload.data || []);
        }
      } catch (_error) {
        if (!cancelled) {
          setSearchError(t("shared.admin_topbar.search.unavailable"));
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }, 2000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchEnabled, searchTerm, workspaceSchool]);

  useEffect(() => {
    if (!platformUser) {
      setNotifications([]);
      setUnreadNotificationCount(0);
      return undefined;
    }

    let cancelled = false;

    async function loadNotifications() {
      try {
        const response = await fetch("/api/v1/admin/notifications?per_page=5", {
          headers: {
            Accept: "application/json"
          },
          credentials: "same-origin"
        });
        const payload = await readJson(response);
        if (cancelled) {
          return;
        }

        setNotifications(payload.data || []);
        setUnreadNotificationCount(payload.meta?.unread_count || 0);
      } catch (_error) {
        if (!cancelled) {
          setNotifications([]);
          setUnreadNotificationCount(0);
        }
      }
    }

    loadNotifications();
    const intervalId = window.setInterval(loadNotifications, 30000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [platformUser]);

  useEffect(() => {
    const appId = oneSignalAppId();
    if (!pushEnabled || !appId || typeof window === "undefined") {
      return undefined;
    }

    const scriptId = "school-bridge-onesignal-sdk";
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
      script.defer = true;
      document.head.appendChild(script);
    }

    let disposed = false;
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      if (disposed) {
        return;
      }

      if (!window.__schoolBridgeAdminOneSignalInitialized) {
        await OneSignal.init({
          appId,
          allowLocalhostAsSecureOrigin: true,
          serviceWorkerPath: "/OneSignalSDKWorker.js",
          notifyButton: { enable: false },
          promptOptions: {
            slidedown: {
              prompts: [
                {
                  type: "push",
                  autoPrompt: false,
                  text: {
                    actionMessage: "Subscribe to admin notifications for new messages, operational updates, and important school activity. You can disable them anytime.",
                    acceptButton: "Allow notifications",
                    cancelButton: "Not now"
                  }
                }
              ]
            }
          }
        });
        window.__schoolBridgeAdminOneSignalInitialized = true;
      }

      if (currentUser?.id) {
        OneSignal.login?.(String(currentUser.id));
      }

      try {
        const permissionStatus = await OneSignal.Notifications?.permissionNative?.();
        const canPrompt = await OneSignal.Notifications?.canRequestPermission?.();
        if ((permissionStatus == null || permissionStatus === 0 || permissionStatus === "default") && canPrompt) {
          OneSignal.Slidedown?.promptPush?.();
        }
      } catch (_error) {
        // no-op
      }

      const syncDevice = async () => {
        const subscription = OneSignal.User?.PushSubscription || OneSignal.User?.pushSubscription;
        const rawPermissionStatus = OneSignal.Notifications?.permission;
        const permissionStatus = rawPermissionStatus === true || rawPermissionStatus === "granted"
          ? "granted"
          : rawPermissionStatus === false || rawPermissionStatus === "denied"
            ? "denied"
            : "not_determined";
        const subscriptionId = subscription?.id || await subscription?.getIdAsync?.().catch(() => null);
        const pushToken = subscription?.token || await subscription?.getTokenAsync?.().catch(() => null);
        const oneSignalUserId = OneSignal.User?.onesignalId || await OneSignal.User?.getOnesignalId?.().catch(() => null);

        const response = await fetch("/api/v1/admin/push_device", {
          method: "PATCH",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken()
          },
          body: JSON.stringify({
            push_device: {
              workspace_school_id: workspaceSchool?.id,
              device_identifier: adminPushDeviceIdentifier(),
              permission_status: permissionStatus,
              one_signal_subscription_id: subscriptionId,
              one_signal_user_id: oneSignalUserId,
              push_token: pushToken,
              session_active: true
            }
          })
        });

        return readJson(response).catch(() => null);
      };

      await syncDevice().catch(() => null);
      OneSignal.Notifications?.addEventListener?.("click", (event) => {
        const additionalData = event?.notification?.additionalData || event?.notification?.data || {};
        if (additionalData?.target_screen === "messages" && additionalData?.message_thread_id && additionalData?.school_id) {
          window.location.assign(`/admin/messages/${additionalData.message_thread_id}?workspace_school_id=${additionalData.school_id}`);
          return;
        }

        if (additionalData?.target_screen === "subscription" && additionalData?.tenant_subscription_id && additionalData?.school_id) {
          window.location.assign(`/admin/schools/${additionalData.school_id}/workspace/subscription?subscription_id=${additionalData.tenant_subscription_id}`);
        }
      });
      OneSignal.Notifications?.addEventListener?.("permissionChange", () => syncDevice().catch(() => null));
      (OneSignal.User?.PushSubscription || OneSignal.User?.pushSubscription)?.addEventListener?.("change", () => syncDevice().catch(() => null));
    });

    return () => {
      disposed = true;
    };
  }, [pushEnabled, currentUser?.id, workspaceSchool?.id]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!searchContainerRef.current?.contains(event.target)) {
        setSearchOpen(false);
      }

      if (!localeContainerRef.current?.contains(event.target)) {
        setLocaleOpen(false);
      }

      if (!notificationsContainerRef.current?.contains(event.target)) {
        setNotificationsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function handleOpenNotification(notification) {
    if (!notification?.id) {
      return;
    }

    if (!notification.read_at) {
      try {
        const response = await fetch(`/api/v1/admin/notifications/${notification.id}`, {
          method: "PATCH",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": csrfToken()
          }
        });
        await readJson(response);
        setNotifications((current) => current.map((entry) => (
          entry.id === notification.id ? { ...entry, read_at: new Date().toISOString() } : entry
        )));
        setUnreadNotificationCount((current) => Math.max(current - 1, 0));
      } catch (_error) {
        return;
      }
    }

    setNotificationsOpen(false);
    if (notification.href && notification.href !== "#") {
      window.location.assign(notification.href);
    }
  }

  async function handleMarkAllNotificationsRead() {
    try {
      const response = await fetch("/api/v1/admin/notifications/mark_all_read", {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken()
        }
      });
      await readJson(response);
      setNotifications((current) => current.map((entry) => ({ ...entry, read_at: entry.read_at || new Date().toISOString() })));
      setUnreadNotificationCount(0);
    } catch (_error) {
      // no-op
    }
  }

  return (
    <>
      <header className="relative z-[10] border-b border-slate-200 bg-white/80 px-5 py-4 backdrop-blur lg:px-8">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div ref={searchContainerRef} className="relative w-full max-w-2xl">
            <label className="relative block w-full">
              <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-slate-400">
                <i className="fa-solid fa-magnifying-glass" />
              </span>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onFocus={() => {
                  if (searchEnabled && searchTerm.trim().length >= 2) {
                    setSearchOpen(true);
                  }
                }}
                placeholder={!searchEnabled
                  ? t("shared.admin_topbar.search.unavailable")
                  : workspaceSchool
                  ? t("shared.admin_topbar.search.workspace_placeholder", { school_name: workspaceSchool.name })
                  : t("shared.admin_topbar.search.platform_placeholder")}
                disabled={!searchEnabled}
                className="h-14 w-full rounded-[22px] border border-slate-200 bg-slate-50 pl-12 pr-28 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-brand-300 focus:bg-white"
              />
              {searchLoading ? (
                <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.10em] text-brand-400">
                  <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
                  {t("shared.admin_topbar.search.thinking")}
                </span>
              ) : null}
            </label>

            {searchEnabled && searchOpen && searchTerm.trim().length >= 2 ? (
              <div className="absolute left-0 right-0 z-[140] mt-3 max-h-[70vh] overflow-y-auto rounded-[28px] border border-slate-200 bg-white p-3 shadow-[0_24px_60px_rgba(15,23,42,0.14)]">
                {searchError ? (
                  <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-4 py-4 text-sm font-medium text-rose-700">
                    {searchError}
                  </div>
                ) : searchLoading ? (
                  <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-500">
                    {t("shared.admin_topbar.search.preparing")}
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-500">
                    {t("shared.admin_topbar.search.no_results", { term: searchTerm.trim() }).split(searchTerm.trim())[0]}
                    <span className="font-semibold text-slate-700">{searchTerm.trim()}</span>
                    {t("shared.admin_topbar.search.no_results", { term: searchTerm.trim() }).split(searchTerm.trim())[1]}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {searchResults.map((group) => (
                      <div key={group.entity_key} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">{group.label}</p>
                            <p className="mt-1 text-sm text-slate-500">
                              {group.items.length === 1
                                ? t("shared.admin_topbar.search.found_matches_one", { count: group.items.length })
                                : t("shared.admin_topbar.search.found_matches_other", { count: group.items.length })}
                            </p>
                          </div>
                          <a
                            href={group.href}
                            className="inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-700 transition hover:bg-brand-50"
                          >
                            {t("shared.admin_topbar.search.more")}
                            <i className="fa-solid fa-arrow-right" />
                          </a>
                        </div>

                        <div className="mt-4 space-y-2">
                          {group.items.map((item, index) => (
                            <a
                              key={`${group.entity_key}-${item.title}-${index}`}
                              href={item.href}
                              className="block rounded-[20px] border border-slate-200 bg-white px-4 py-4 transition hover:border-brand-200 hover:bg-brand-50/40"
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-950">{item.title}</p>
                                  <p className="mt-1 truncate text-sm text-slate-500">{item.subtitle}</p>
                                </div>
                                <i className="fa-solid fa-arrow-up-right-from-square text-xs text-slate-300" />
                              </div>
                              {item.matched_in?.length ? (
                                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.18em] text-brand-700">
                                  {t("shared.admin_topbar.search.found_in", { fields: item.matched_in.join(", ") })}
                                </p>
                              ) : null}
                            </a>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-3">
            <div className="hidden items-center gap-3 rounded-[22px] px-4 py-3 lg:flex">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-sm font-semibold text-brand-700">
                {initialsForUser(currentUser)}
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-900">{displayNameForUser(currentUser)}</p>
                <p className="text-xs text-slate-500">{currentUser?.roleLabel || t("shared.admin_topbar.current_user.platform_user")}</p>
              </div>
            </div>

            <div ref={localeContainerRef} className="relative">
              <button
                type="button"
                onClick={() => setLocaleOpen((open) => !open)}
                className="flex h-14 min-w-[5.5rem] items-center justify-center gap-2 rounded-[22px] px-4 text-slate-700 transition hover:border-brand-200 hover:bg-white hover:text-brand-700"
                aria-label={t("shared.admin_topbar.locale.switch_language")}
                title={selectedLocale?.label}
              >
                <span className="text-lg leading-none">{selectedLocale?.flag}</span>
                <i className="fa-solid fa-chevron-down text-[10px]" />
              </button>

              {localeOpen ? (
                <div className="absolute right-0 z-[140] mt-3 w-48 rounded-[24px] border border-slate-200 bg-white p-2 shadow-[0_24px_60px_rgba(15,23,42,0.14)]">
                  {localeOptions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => switchLocale(option.key)}
                      className={`flex w-full items-center gap-3 rounded-[18px] px-4 py-3 text-left text-sm font-semibold transition ${
                        option.key === currentLocale
                          ? "bg-brand-50 text-brand-700"
                          : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                      }`}
                    >
                      <span className="text-lg leading-none">{option.flag}</span>
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {platformUser ? (
            <div ref={notificationsContainerRef} className="relative">
              <button
                type="button"
                onClick={() => setNotificationsOpen((open) => !open)}
                className="relative flex h-14 w-14 items-center justify-center rounded-[22px] text-slate-700 transition hover:border-brand-200 hover:bg-white hover:text-brand-700"
                aria-label={t("shared.admin_topbar.notifications.toggle")}
              >
                <i className="fa-solid fa-bell" />
                {unreadNotificationCount > 0 ? (
                  <span className="absolute right-3 top-3 h-2.5 w-2.5 rounded-full bg-accent-500" />
                ) : null}
              </button>

              {notificationsOpen ? (
                <div className="absolute right-0 z-[140] mt-3 w-[360px] max-w-[calc(100vw-2rem)] rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_24px_60px_rgba(15,23,42,0.14)]">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">{t("shared.admin_topbar.notifications.title")}</p>
                      <h3 className="mt-2 text-lg font-semibold text-slate-950">{t("shared.admin_topbar.notifications.recent_updates")}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href="/admin/notifications"
                        className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
                        aria-label={t("shared.admin_topbar.notifications.view_all")}
                        title={t("shared.admin_topbar.notifications.view_all")}
                      >
                        <i className="fa-solid fa-eye" />
                      </a>
                      <button
                        type="button"
                        onClick={handleMarkAllNotificationsRead}
                        className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 transition hover:bg-slate-200 hover:text-slate-900"
                        aria-label={t("shared.admin_topbar.notifications.mark_all_read")}
                        title={t("shared.admin_topbar.notifications.mark_all_read")}
                      >
                        <i className="fa-solid fa-check-double" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setNotificationsOpen(false)}
                        className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
                        aria-label={t("shared.admin_topbar.notifications.close")}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    {notifications.length === 0 ? (
                      <div className="rounded-[22px] border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
                        {t("shared.admin_topbar.notifications.empty")}
                      </div>
                    ) : notifications.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => handleOpenNotification(item)}
                        className={`block w-full rounded-[22px] border px-4 py-4 text-left transition ${
                          item.read_at ? "border-slate-200 bg-slate-50" : "border-brand-200 bg-brand-50/60"
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-slate-600">
                            <i className={`fa-solid ${notificationIconClass(item.category_key)}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                            <p className="mt-1 text-sm leading-6 text-slate-500">{item.body}</p>
                          </div>
                          {!item.read_at ? <span className="mt-2 h-2.5 w-2.5 rounded-full bg-brand-600" /> : null}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            ) : null}

            <button
              type="button"
              onClick={() => setConfirmSignOutOpen(true)}
              className="flex h-14 w-14 items-center justify-center rounded-[22px] text-slate-700 transition hover:border-red-200 hover:bg-white hover:text-red-600"
              aria-label={t("shared.admin_topbar.sign_out.label")}
            >
              <i className="fa-solid fa-right-from-bracket" />
            </button>
          </div>
        </div>
      </header>

      <ConfirmationModal
        open={confirmSignOutOpen}
        title={t("shared.admin_topbar.sign_out.title", { application_name: resolvedApplicationName })}
        message={t("shared.admin_topbar.sign_out.message")}
        confirmLabel={t("shared.admin_topbar.sign_out.label")}
        confirmingLabel={t("shared.admin_topbar.sign_out.confirming")}
        onCancel={() => setConfirmSignOutOpen(false)}
        onConfirm={submitSignOut}
      />
    </>
  );
}
